import {
  formatCurrency,
  formatDate,
  NoWrap,
  numberFormat,
  transformDailyData,
  transformMonthlyData,
  transformWeeklyData,
  transformYearlyData,
} from "../../utils/helpers";
import { PaymentItemTag } from "../../components/common/registerUtils";
import { InputType } from "../../types/common";
import {
  DAILY_PAYMENT_ITEM_CHANNEL_REPORT,
  MONTHLY_PAYMENT_ITEM_CHANNEL_REPORT,
} from "../../utils/apis";

type ReportType = "daily" | "weekly" | "monthly" | "yearly";
type TotalType = "total_amount" | "total_count" | undefined;

const getTableApiForReportType = (reportType: ReportType): string =>
  reportType === "daily" || reportType === "weekly"
    ? DAILY_PAYMENT_ITEM_CHANNEL_REPORT
    : MONTHLY_PAYMENT_ITEM_CHANNEL_REPORT;

const getGraphApiForReportType = (reportType: ReportType): string =>
  getTableApiForReportType(reportType) + "graphic-data/";

export const usePaymentItemChannelReport = () => {
  return {
    reportTitle: "Transaction Summary",

    customApi: (reportType: ReportType) => getTableApiForReportType(reportType),

    customGraphApi: (reportType: ReportType) =>
      getGraphApiForReportType(reportType),

    tableData: (results: any[], reportType: ReportType) => {
      if (reportType === "weekly") {
        const grouped: Record<string, any> = {};

        results.forEach((item: any) => {
          const week = Math.ceil(item.day_of_month / 7);
          const key = `${week}-${item.item_name}`;

          if (!grouped[key]) {
            grouped[key] = {
              total_amount: 0,
              total_count: 0,
              item_name: item.item_name,
            };
          }

          grouped[key].total_amount += Number(item.total_amount || 0);
          grouped[key].total_count += Number(item.total_count || 0);
        });

        return Object.entries(grouped)
          .sort(([a], [b]) => {
            const [weekA, channelA] = a.split("-");
            const [weekB, channelB] = b.split("-");
            return (
              Number(weekA) - Number(weekB) || channelA.localeCompare(channelB)
            );
          })
          .map(([key, item]: [string, any]) => {
            const [week] = key.split("-");
            return {
              week_of_month: `Week ${week}`,
              total_amount: formatCurrency(item.total_amount),
              total_count: numberFormat(item.total_count),
              item_name: <PaymentItemTag item_name={item.item_name} />,
            };
          });
      }

      if (reportType === "yearly") {
        const grouped: Record<string, any> = {};

        results.forEach((item: any) => {
          const key = `${item.year_value}-${item.item_name}`;

          if (!grouped[key]) {
            grouped[key] = {
              total_amount: 0,
              total_count: 0,
              item_name: item.item_name,
              year: item.year_value,
            };
          }

          grouped[key].total_amount += Number(item.total_amount || 0);
          grouped[key].total_count += Number(item.total_count || 0);
        });

        return Object.values(grouped)
          .sort(
            (a: any, b: any) =>
              Number(a.year) - Number(b.year) ||
              a.item_name.localeCompare(b.item_name),
          )
          .map((item: any) => ({
            year_value: item.year,
            total_amount: formatCurrency(item.total_amount),
            total_count: numberFormat(item.total_count),
            item_name: <PaymentItemTag item_name={item.item_name} />,
          }));
      }

      // daily / monthly
      return results.map((item: any) => ({
        ...item,
        date: (
          <NoWrap>
            {reportType === "daily"
              ? formatDate(item.date)
              : item.month_name || item.date}
          </NoWrap>
        ),
        total_amount: formatCurrency(item.total_amount || "0"),
        total_count: numberFormat(item.total_count || "0"),
        item_name: <PaymentItemTag item_name={item.item_name} />,
      }));
    },

    tableColumns: (reportType: ReportType) => [
      ...(reportType === "daily" ? [{ id: "date", header: "Date" }] : []),
      ...(reportType === "monthly"
        ? [{ id: "month_name", header: "Month" }]
        : []),
      ...(reportType === "weekly"
        ? [{ id: "week_of_month", header: "Week" }]
        : []),
      ...(reportType === "yearly"
        ? [{ id: "year_value", header: "Year" }]
        : []),
      { id: "total_amount", header: "Total Amount", align: "right" },
      { id: "total_count", header: "Total Count" },
      {
        id: "item_name",
        header: "Payment Item",
        filter: {
          type: "select",
          options: [
            { id: "MTN deposits", name: "MTN deposits" },
            { id: "NWSC", name: "NWSC" },
            { id: "URA", name: "URA" },
            { id: "AIRTEL deposits", name: "AIRTEL deposits" },
            { id: "Wallent Payment", name: "Wallent Payment" },
          ],
        },
      },
    ],

    graphData: (
      results: any[],
      reportType: ReportType,
      totalType: TotalType,
    ) => {
      if (!results.length) return { xLabels: [], series: [], yLabel: "" };

      let transformed;
      if (reportType === "daily")
        transformed = transformDailyData(results, totalType);
      else if (reportType === "weekly")
        transformed = transformWeeklyData(results, totalType);
      else if (reportType === "yearly")
        transformed = transformYearlyData(results, totalType);
      else transformed = transformMonthlyData(results, totalType);

      return {
        ...transformed,
        yLabel: totalType === "total_amount" ? "Total Amount" : "Total Count",
      };
    },

    externalFilters: [
      {
        id: "start_date",
        header: "Date Range",
        type: "date" as InputType,
        rangeNames: {
          startKey: "start_date",
          endKey: "end_date",
        },
      },
    ],

    initialFilters: {
      item_name: "MTN deposits",
    },

    exportData: {
      /**
       * Returns the correct export endpoint for the active report type,
       * mirroring the same daily / monthly split used for data fetching.
       */
      getEndpoint: (reportType: ReportType) =>
        reportType === "daily" || reportType === "weekly"
          ? `${DAILY_PAYMENT_ITEM_CHANNEL_REPORT}export-to-csv`
          : `${MONTHLY_PAYMENT_ITEM_CHANNEL_REPORT}export-to-csv`,
      fileName: "payment-item-channel-report.xlsx",
      buttonText: "Export CSV",
    },

    totalTypeOptions: [
      { label: "Total Amount", value: "total_amount" },
      { label: "Total Count", value: "total_count" },
    ],

    showReportTypeFilter: true,
    showTotalTypeFilter: true,
    showMonthFilter: true,
    showYearFilter: true,
    defaultReportType: "monthly" as ReportType,
    defaultTotalType: "total_amount" as TotalType,
  };
};
